"use client"

import { useState, useRef, type FormEvent } from "react"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { toast } from "sonner"
import { CheckCircle, Upload, X, FileText, AlertCircle, Loader2 } from 'lucide-react'
import { Progress } from "../ui/progress"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "../lib/utils"

interface UploadDocumentsFormProps {
  onSuccess?: () => void
}

export function UploadDocumentsForm({ onSuccess }: UploadDocumentsFormProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "success" | "error">("idle")
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropzoneRef = useRef<HTMLDivElement>(null)

  // Simulate progress for better UX
  const simulateProgress = () => {
    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        const newProgress = prevProgress + Math.random() * 5
        if (newProgress >= 95) {
          clearInterval(interval)
          return 95
        }
        return newProgress
      })
    }, 300)

    return interval
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0])
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (dropzoneRef.current) {
      dropzoneRef.current.classList.add("border-primary")
      dropzoneRef.current.classList.add("bg-primary/5")
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (dropzoneRef.current) {
      dropzoneRef.current.classList.remove("border-primary")
      dropzoneRef.current.classList.remove("bg-primary/5")
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (dropzoneRef.current) {
      dropzoneRef.current.classList.remove("border-primary")
      dropzoneRef.current.classList.remove("bg-primary/5")
    }
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0])
      if (fileInputRef.current) {
        // Create a DataTransfer object and add the file
        const dataTransfer = new DataTransfer()
        dataTransfer.items.add(e.dataTransfer.files[0])
        fileInputRef.current.files = dataTransfer.files
      }
    }
  }

  const handleFileUpload = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)
    setProgress(0)
    setUploadStatus("uploading")

    const formData = new FormData(e.currentTarget)
    const file = formData.get("file") as File
    
    if (!file) {
      toast.error("Please select a file to upload", {
        icon: <AlertCircle className="h-5 w-5 text-red-500" />,
      })
      setIsLoading(false)
      setUploadStatus("idle")
      return
    }

    // Start progress simulation
    const progressInterval = simulateProgress()

    try {
    const response = await fetch("/api/retrieval/ingest", {
      method: "POST",
        body: formData,
      })

      // Complete the progress bar
      clearInterval(progressInterval)
      setProgress(100)

    if (response.status === 200) {
        setUploadStatus("success")
        
        // Show success toast
        toast.success("Document uploaded successfully!", {
          icon: <CheckCircle className="h-5 w-5 text-green-500" />,
          description: `${file.name} has been processed and is ready to use.`,
        })

        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess()
        }

        // Reset form after 1.5 seconds
        setTimeout(() => {
          resetForm()
        }, 1500)
    } else {
        const json = await response.json()
        setUploadStatus("error")
        toast.error(json.error || "Failed to process document", {
          icon: <AlertCircle className="h-5 w-5 text-red-500" />,
        })
      }
    } catch (error) {
      setUploadStatus("error")
      toast.error("Error uploading document", {
        icon: <AlertCircle className="h-5 w-5 text-red-500" />,
        description: "Please check your connection and try again.",
      })
    } finally {
      setIsLoading(false)
      clearInterval(progressInterval)
    }
  }

  const resetForm = () => {
    setSelectedFile(null)
    setProgress(0)
    setUploadStatus("idle")
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  return (
    <form onSubmit={handleFileUpload} className="flex flex-col gap-4 w-full">
      <AnimatePresence mode="wait">
        {uploadStatus !== "success" ? (
          <motion.div
            key="upload-form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-2">
              <div
                ref={dropzoneRef}
                className={cn(
                  "border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer",
                  "backdrop-blur-sm"
                )}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="h-6 w-6 text-primary" />
                    <span className="font-medium text-sm">{selectedFile.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 rounded-full ml-2"
                      onClick={(e) => {
                        e.stopPropagation()
                        resetForm()
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="h-8 w-8 text-gray-400" />
                    <p className="text-sm font-medium">Click to select or drag and drop</p>
                    <p className="text-xs text-muted-foreground">
                      PDF, DOC, DOCX, TXT, CSV, XLSX, XLS, JSON, MD, HTML, URL
                    </p>
                  </div>
                )}
              </div>
              <Input
                ref={fileInputRef}
                type="file"
                name="file"
                accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.json,.md,.html,.url"
                className="hidden"
                onChange={handleFileChange}
                disabled={isLoading}
              />
            </div>

            {isLoading && (
              <div className="w-full">
                <div className="flex justify-between text-xs mb-1">
                  <span>Uploading...</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            )}

            <div className="flex justify-end">
              <Button type="submit" disabled={isLoading || !selectedFile} className="gap-2">
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    <span>Upload</span>
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center py-6 gap-3"
          >
            <div className="rounded-full bg-green-100 p-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
            <h3 className="text-lg font-semibold">Upload Complete!</h3>
            <p className="text-sm text-muted-foreground text-center">
              Your document has been successfully uploaded and processed.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  )
}
